import React from 'react';

interface MoonPhaseIconProps {
  phase: number;
  cycle: number;
  name: string;
  size?: number;
  showLabel?: boolean;
}

const MOON_COLORS: Record<string, { light: string; dark: string }> = {
  'Lumenis': { light: '#f5e6b8', dark: '#3a3520' },
  'Umbrath': { light: '#c0b8d0', dark: '#2a2535' },
  'Manith':  { light: '#b8d8f0', dark: '#1a2a35' },
};

const getPhaseName = (phase: number, cycle: number): string => {
  const ratio = phase / cycle;
  if (ratio < 0.03 || ratio > 0.97) return 'Neumond';
  if (ratio < 0.22) return 'Zunehmende Sichel';
  if (ratio < 0.28) return 'Erstes Viertel';
  if (ratio < 0.47) return 'Zunehmend';
  if (ratio < 0.53) return 'Vollmond';
  if (ratio < 0.72) return 'Abnehmend';
  if (ratio < 0.78) return 'Letztes Viertel';
  return 'Abnehmende Sichel';
};

const MoonPhaseIcon: React.FC<MoonPhaseIconProps> = ({ phase, cycle, name, size = 18, showLabel = false }) => {
  const ratio = phase / cycle;
  const colors = MOON_COLORS[name] || { light: '#e8e0c0', dark: '#2a2520' };
  const phaseName = getPhaseName(phase, cycle);

  // Calculate the illumination path
  // ratio 0 = new moon (dark), 0.5 = full moon (light)
  const illumination = ratio <= 0.5 ? ratio * 2 : (1 - ratio) * 2;
  const isWaxing = ratio <= 0.5;

  // SVG path for moon illumination
  const r = size / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;

  // Calculate the terminator curve
  const terminatorX = r * (1 - 2 * illumination);

  let lightPath: string;
  if (illumination >= 0.99) {
    // Full moon
    lightPath = `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r}`;
  } else if (illumination <= 0.01) {
    // New moon - no light
    lightPath = '';
  } else if (isWaxing) {
    // Waxing: light from right
    lightPath = `M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx} ${cy + r} A ${Math.abs(terminatorX)} ${r} 0 0 ${illumination > 0.5 ? 0 : 1} ${cx} ${cy - r}`;
  } else {
    // Waning: light from left
    lightPath = `M ${cx} ${cy - r} A ${r} ${r} 0 0 0 ${cx} ${cy + r} A ${Math.abs(terminatorX)} ${r} 0 0 ${illumination > 0.5 ? 1 : 0} ${cx} ${cy - r}`;
  }

  return (
    <div
      className="moon-phase-icon"
      title={`${name}: ${phaseName} (Tag ${phase + 1}/${cycle})`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: showLabel ? 4 : 0 }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Dark background circle */}
        <circle cx={cx} cy={cy} r={r} fill={colors.dark} stroke="#555" strokeWidth="0.5" />
        {/* Light portion */}
        {lightPath && (
          <path d={lightPath} fill={colors.light} />
        )}
      </svg>
      {showLabel && (
        <span style={{ fontSize: size * 0.6, color: '#888', whiteSpace: 'nowrap' }}>
          {name}
        </span>
      )}
    </div>
  );
};

export default MoonPhaseIcon;
